// Versão: 1.05
// Data..: 2026-10-14
// v1.05: alerta 'update' do staff passa a disparar quando é o responsável OU quando o
//        chamado não tem dono (chamados "soltos"); disparo de new Notification blindado
//        com try/catch (não interrompe o laço nem o som/sino).
// v1.04: notificação nativa usa o volume configurado; staff também recebe alerta de
//        nova resposta nos chamados em que é responsável/seguidor (kind 'update'); novo
//        kind 'agenda' avisa compromissos próximos (GET /me/calendar, só staff).
// v1.03: alerta de cadastro incompleto leva à aba Faturamento Inter (/admin?tab=invoicing).
// v1.02: alerta de cadastro de pagador incompleto para cobrança (kind 'cadastro' +
//        linkTo), via polling admin de /invoices/payer-pending.
// Central de notificações do portal (polling leve, sem infra nova).
// Equipe: novos chamados, mensagens de WhatsApp e tarefas atribuídas.
// Cliente: atualizações (respostas) nos próprios chamados.
// Toca som e dispara notificação nativa conforme as preferências (localStorage).

import { useCallback, useEffect, useRef, useState } from 'react';
import { Channel, TicketStatus, type Ticket, type Paginated, type CalendarEventView } from '@makweb/shared';
import { api } from './api.js';
import { playSound } from './sounds.js';
import { loadPrefs, type NotificationPrefs } from './notificationPrefs.js';

export type NotificationKind = 'ticket' | 'whatsapp' | 'task' | 'update' | 'cadastro' | 'agenda';

// Janela (ms) para avisar compromissos "em breve" na agenda.
const AGENDA_LOOKAHEAD_MS = 15 * 60 * 1000;

export interface AppNotification {
  id: string; // chave única (kind + entidade + carimbo)
  kind: NotificationKind;
  title: string;
  body: string;
  ticketId: string; // vazio quando a notificação não é de chamado (ex.: cadastro)
  createdAt: string; // ISO
  read: boolean;
  // Destino de navegação ao clicar. Quando ausente, navega para o chamado/WhatsApp.
  linkTo?: string;
}

// Empresa com cadastro de pagador incompleto (retorno de /invoices/payer-pending).
interface PayerPendingRow {
  companyId: string;
  companyName: string;
  missing: string[];
}

const POLL_MS = 30_000;
const MAX_ITEMS = 50;

// Status ativos usados para varrer a fila (mesma lógica das telas existentes).
const ACTIVE_STATUSES: TicketStatus[] = [
  TicketStatus.OPEN,
  TicketStatus.IN_PROGRESS,
  TicketStatus.WAITING_CLIENT,
];

interface UseNotificationsResult {
  notifications: AppNotification[];
  unreadCount: number;
  markAllRead: () => void;
  markRead: (id: string) => void;
  clearAll: () => void;
}

// Busca os chamados ativos (todas as filas relevantes) em paralelo.
async function fetchActiveTickets(): Promise<Ticket[]> {
  const lists = await Promise.all(
    ACTIVE_STATUSES.map((s) =>
      api.get<Paginated<Ticket>>(`/tickets?status=${s}&limit=100`).catch(() => ({ items: [] as Ticket[] })),
    ),
  );
  return lists.flatMap((l) => l.items);
}

export function useNotifications(enabled: boolean, currentUserId?: string, isStaff = true): UseNotificationsResult {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  // Estado conhecido entre ciclos: ids de tickets já vistos e último lastMessageAt por ticket.
  const knownTickets = useRef<Set<string> | null>(null);
  const lastMsgAt = useRef<Map<string, string>>(new Map());
  const knownTasks = useRef<Set<string> | null>(null);
  // Empresas com cadastro de pagador incompleto já notificadas (dedupe por companyId).
  const knownPendingPayers = useRef<Set<string> | null>(null);
  // Compromissos da agenda já avisados nesta sessão (dedupe por uid).
  const knownAgenda = useRef<Set<string>>(new Set());
  const prefs = useRef<NotificationPrefs>(loadPrefs());

  // Mantém as preferências atualizadas em tempo real quando mudam em Configurações.
  useEffect(() => {
    const onPrefs = (e: Event) => { prefs.current = (e as CustomEvent<NotificationPrefs>).detail; };
    window.addEventListener('makweb:notif-prefs', onPrefs as EventListener);
    return () => window.removeEventListener('makweb:notif-prefs', onPrefs as EventListener);
  }, []);

  // Adiciona notificações novas, dispara som/desktop e limita o histórico.
  const push = useCallback((items: AppNotification[]) => {
    if (!items.length) return;
    setNotifications((cur) => [...items, ...cur].slice(0, MAX_ITEMS));
    const p = prefs.current;
    if (p.soundEnabled) playSound(p.sound, p.volume);
    if (p.desktopEnabled && 'Notification' in window && Notification.permission === 'granted') {
      for (const n of items.slice(0, 3)) {
        // Alguns navegadores lançam exceção ao criar a notificação (política/foco).
        // Blindamos o disparo para não interromper o laço nem o som/sino já tocados.
        try {
          new Notification(n.title, { body: n.body, tag: n.id });
        } catch (err) {
          console.warn('Falha ao exibir notificação do sistema', err);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => undefined);
    }

    let active = true;

    const poll = async () => {
      try {
        const tickets = await fetchActiveTickets();
        if (!active) return;

        const ids = new Set(tickets.map((t) => t.id));
        const fresh: AppNotification[] = [];
        const firstRun = knownTickets.current === null;

        for (const t of tickets) {
          const stamp = t.lastMessageAt ?? t.updatedAt;
          const prevStamp = lastMsgAt.current.get(t.id);

          if (!firstRun) {
            const isNew = !knownTickets.current!.has(t.id);
            if (isStaff) {
              // Equipe: novos chamados e novas mensagens de WhatsApp.
              if (isNew) {
                if (t.channel === Channel.WHATSAPP) {
                  fresh.push({
                    id: `wa-${t.id}-${stamp}`, kind: 'whatsapp',
                    title: `WhatsApp — ${t.requesterName ?? t.requesterContact ?? 'Contato'}`,
                    body: t.subject, ticketId: t.id, createdAt: stamp, read: false,
                  });
                } else {
                  fresh.push({
                    id: `tk-${t.id}`, kind: 'ticket',
                    title: `Novo chamado #${t.number}`,
                    body: t.subject, ticketId: t.id, createdAt: stamp, read: false,
                  });
                }
              } else if (prevStamp && stamp > prevStamp && t.channel === Channel.WHATSAPP) {
                fresh.push({
                  id: `wa-${t.id}-${stamp}`, kind: 'whatsapp',
                  title: `Nova mensagem — ${t.requesterName ?? t.requesterContact ?? 'Contato'}`,
                  body: t.subject, ticketId: t.id, createdAt: stamp, read: false,
                });
              } else if (
                // Nova resposta num chamado NÃO-WhatsApp. Agora notificamos quando (a) o
                // staff é o responsável OU (b) o chamado ainda NÃO tem dono (assigneeId
                // vazio/undefined) — assim respostas em chamados "soltos" não passam
                // despercebidas. Limitação: o payload do polling (Ticket) não traz o autor
                // da última mensagem, então não dá para evitar com certeza a própria
                // resposta do atendente; (a)||(b) é a melhor aproximação disponível.
                !isNew &&
                prevStamp &&
                stamp > prevStamp &&
                t.channel !== Channel.WHATSAPP &&
                (!t.assigneeId || (!!currentUserId && t.assigneeId === currentUserId))
              ) {
                fresh.push({
                  id: `upd-${t.id}-${stamp}`, kind: 'update',
                  title: `Nova resposta no chamado #${t.number}`,
                  body: t.subject, ticketId: t.id, createdAt: stamp, read: false,
                });
              }
            } else {
              // Cliente: atualização nos próprios chamados (nova resposta/mudança).
              if (!isNew && prevStamp && stamp > prevStamp) {
                fresh.push({
                  id: `upd-${t.id}-${stamp}`, kind: 'update',
                  title: `Atualização no chamado #${t.number}`,
                  body: t.subject, ticketId: t.id, createdAt: stamp, read: false,
                });
              }
            }
          }
          lastMsgAt.current.set(t.id, stamp);
        }

        // Tarefas atribuídas ao usuário (equipe): detecta novas atribuições.
        if (isStaff && currentUserId) {
          const myTasks = tickets.filter((t) => t.isTask && t.assigneeId === currentUserId);
          const taskIds = new Set(myTasks.map((t) => t.id));
          if (knownTasks.current !== null) {
            for (const t of myTasks) {
              if (!knownTasks.current.has(t.id)) {
                fresh.push({
                  id: `task-${t.id}`, kind: 'task',
                  title: `Tarefa atribuída a você`,
                  body: `#${t.number} — ${t.subject}`, ticketId: t.id,
                  createdAt: t.updatedAt, read: false,
                });
              }
            }
          }
          knownTasks.current = taskIds;
        }

        // Empresas com cadastro de pagador incompleto para cobrança (admin). A rota é
        // admin-only; para não-admin retorna 403 e é ignorada no catch (não quebra a UI).
        if (isStaff) {
          try {
            const pending = await api.get<PayerPendingRow[]>('/invoices/payer-pending');
            const firstPayerRun = knownPendingPayers.current === null;
            const seen = new Set<string>();
            for (const row of pending) {
              seen.add(row.companyId);
              if (!firstPayerRun && !knownPendingPayers.current!.has(row.companyId)) {
                fresh.push({
                  id: `cad-${row.companyId}`,
                  kind: 'cadastro',
                  title: 'Cadastro incompleto para cobrança',
                  body: `${row.companyName} — faltam: ${row.missing.join(', ')}`,
                  ticketId: '',
                  createdAt: new Date().toISOString(),
                  read: false,
                  // Leva direto à aba de Faturamento Inter (onde o alerta é acionável).
                  linkTo: '/admin?tab=invoicing',
                });
              }
            }
            knownPendingPayers.current = seen;
          } catch {
            // sem permissão / falha de rede: ignora
          }
        }

        // Agenda (staff): compromissos que começam nos próximos ~15 min. A rota
        // /me/calendar é staff-only e só funciona com CalDAV configurado; qualquer
        // erro/vazio é ignorado silenciosamente para não quebrar nem poluir o console.
        if (isStaff) {
          try {
            const nowMs = Date.now();
            const fromIso = new Date(nowMs).toISOString();
            const toIso = new Date(nowMs + AGENDA_LOOKAHEAD_MS).toISOString();
            const events = await api
              .get<CalendarEventView[]>(`/me/calendar?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`)
              .catch(() => [] as CalendarEventView[]);
            for (const ev of events) {
              const startMs = Date.parse(ev.start);
              if (Number.isNaN(startMs)) continue;
              // Só o que ainda não começou e cai na janela de antecedência.
              if (startMs < nowMs || startMs > nowMs + AGENDA_LOOKAHEAD_MS) continue;
              if (knownAgenda.current.has(ev.uid)) continue;
              knownAgenda.current.add(ev.uid);
              fresh.push({
                id: `agenda-${ev.uid}`,
                kind: 'agenda',
                title: 'Compromisso em breve',
                body: ev.summary,
                ticketId: '',
                createdAt: ev.start,
                read: false,
                linkTo: '/schedule',
              });
            }
          } catch {
            // CalDAV ausente / falha de rede: ignora
          }
        }

        knownTickets.current = ids;
        if (!firstRun) push(fresh);
      } catch {
        // falha de rede não deve quebrar a UI
      }
    };

    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => { active = false; clearInterval(timer); };
  }, [enabled, currentUserId, isStaff, push]);

  const markAllRead = useCallback(() => {
    setNotifications((cur) => cur.map((n) => ({ ...n, read: true })));
  }, []);
  const markRead = useCallback((id: string) => {
    setNotifications((cur) => cur.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);
  const clearAll = useCallback(() => setNotifications([]), []);

  const unreadCount = notifications.reduce((acc, n) => acc + (n.read ? 0 : 1), 0);
  return { notifications, unreadCount, markAllRead, markRead, clearAll };
}
